'use client';

import { c as _c } from "react/compiler-runtime";
import type { CustomerFunnelStage, DemandChannel, DemandDeliveryActivity, DemandInspirationAttributes, DemandObjective, DemandRole, DemandTactic } from '@sizzlenz/shared';
import { CUSTOMER_FUNNEL_STAGES, DEMAND_CHANNELS, DEMAND_DELIVERY_ACTIVITIES, DEMAND_DELIVERY_DISCIPLINES, DEMAND_OBJECTIVES, DEMAND_ROLES, DEMAND_TACTICS } from '@sizzlenz/shared';
import { ChevronDown, Goal, Lightbulb, Radio, Route, SlidersHorizontal, Target, Wrench, X, type LucideIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
export interface DemandInspirationFilterValue {
  demandRoles: DemandRole[];
  journeyStages: CustomerFunnelStage[];
  demandObjectives: DemandObjective[];
  tactics: DemandTactic[];
  channels: DemandChannel[];
  deliveryActivities: DemandDeliveryActivity[];
}
export const createEmptyDemandInspirationFilters = (): DemandInspirationFilterValue => ({
  demandRoles: [],
  journeyStages: [],
  demandObjectives: [],
  tactics: [],
  channels: [],
  deliveryActivities: []
});
export type DemandInspirationFilterKey = keyof DemandInspirationFilterValue;
interface FilterOption {
  id: string;
  label: string;
  groupLabel?: string;
}
interface FilterFacet {
  key: DemandInspirationFilterKey;
  label: string;
  mobileLabel: string;
  icon: LucideIcon;
  options: FilterOption[];
}
const deliveryDisciplineLabels = new Map(DEMAND_DELIVERY_DISCIPLINES.map(discipline => [discipline.id, discipline.label]));
const FILTER_FACETS: FilterFacet[] = [{
  key: 'demandRoles',
  label: 'Demand role',
  mobileLabel: 'What role does it play?',
  icon: Target,
  options: DEMAND_ROLES.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'journeyStages',
  label: 'Journey stage',
  mobileLabel: 'Where is the consumer?',
  icon: Route,
  options: CUSTOMER_FUNNEL_STAGES.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'demandObjectives',
  label: 'Objective',
  mobileLabel: 'What needs to change?',
  icon: Goal,
  options: DEMAND_OBJECTIVES.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'tactics',
  label: 'Tactic',
  mobileLabel: 'Which approach is being used?',
  icon: Lightbulb,
  options: DEMAND_TACTICS.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'channels',
  label: 'Channel',
  mobileLabel: 'Where does it happen?',
  icon: Radio,
  options: DEMAND_CHANNELS.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'deliveryActivities',
  label: 'Execution',
  mobileLabel: 'What is needed to execute?',
  icon: Wrench,
  options: DEMAND_DELIVERY_ACTIVITIES.map(({
    id,
    label,
    disciplineId
  }) => ({
    id,
    label,
    groupLabel: deliveryDisciplineLabels.get(disciplineId)
  }))
}];
export const DEMAND_INSPIRATION_FILTER_KEYS = FILTER_FACETS.map(facet => facet.key) as DemandInspirationFilterKey[];
const FACET_ICONS = new Map<DemandInspirationFilterKey, LucideIcon>(FILTER_FACETS.map(facet => [facet.key, facet.icon]));
const selectionsMatch = (selected: readonly string[], available: readonly string[]): boolean => selected.length === 0 || selected.some(value => available.includes(value));
export function matchesDemandInspirationFilters(attributes: DemandInspirationAttributes, filters: DemandInspirationFilterValue, visibleFilters: readonly DemandInspirationFilterKey[] = DEMAND_INSPIRATION_FILTER_KEYS): boolean {
  const isVisible = (key: DemandInspirationFilterKey) => visibleFilters.includes(key);
  return (!isVisible('demandRoles') || selectionsMatch(filters.demandRoles, attributes.demandRoles)) && (!isVisible('journeyStages') || selectionsMatch(filters.journeyStages, attributes.journeyStages)) && (!isVisible('demandObjectives') || selectionsMatch(filters.demandObjectives, attributes.demandObjectives)) && (!isVisible('tactics') || selectionsMatch(filters.tactics, attributes.tactics)) && (!isVisible('channels') || selectionsMatch(filters.channels, attributes.channels)) && (!isVisible('deliveryActivities') || selectionsMatch(filters.deliveryActivities, attributes.deliveryActivities));
}
interface DemandInspirationFiltersProps {
  value: DemandInspirationFilterValue;
  onChange: (value: DemandInspirationFilterValue) => void;
  resultCount: number;
  totalCount: number;
  visibleFilters?: readonly DemandInspirationFilterKey[];
  className?: string;
}
export function DemandInspirationFilters(t0) {
  const $ = _c(80);
  const {
    value,
    onChange,
    resultCount,
    visibleFilters: t1,
    className
  } = t0;
  const visibleFilters = t1 === undefined ? DEMAND_INSPIRATION_FILTER_KEYS : t1;
  const [mobileOpen, setMobileOpen] = useState(false);
  let t2;
  if ($[0] !== visibleFilters) {
    t2 = FILTER_FACETS.filter(facet => visibleFilters.includes(facet.key));
    $[0] = visibleFilters;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const visibleFacets = t2;
  let t3;
  if ($[2] !== value) {
    t3 = (count, facet_0) => count + value[facet_0.key].length;
    $[2] = value;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const activeFilterCount = visibleFacets.reduce(t3, 0);
  const hasActiveFilters = activeFilterCount > 0;
  let t4;
  if ($[4] !== value || $[5] !== visibleFacets) {
    let t5;
    if ($[7] !== value) {
      t5 = facet_1 => facet_1.options.filter(option => (value[facet_1.key] as string[]).includes(option.id)).map(option_0 => ({
        facetKey: facet_1.key,
        option: option_0
      }));
      $[7] = value;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = visibleFacets.flatMap(t5);
    $[4] = value;
    $[5] = visibleFacets;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const activeSelections = t4;
  let t5;
  if ($[9] !== onChange || $[10] !== value) {
    t5 = (key, id) => {
      const selected = value[key] as string[];
      const next = selected.includes(id) ? selected.filter(selectedId => selectedId !== id) : [...selected, id];
      onChange({
        ...value,
        [key]: next
      } as DemandInspirationFilterValue);
    };
    $[9] = onChange;
    $[10] = value;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const toggleSelection = t5;
  let t6;
  if ($[12] !== onChange || $[13] !== value) {
    t6 = key_0 => {
      onChange({
        ...value,
        [key_0]: []
      } as DemandInspirationFilterValue);
    };
    $[12] = onChange;
    $[13] = value;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const clearFacet = t6;
  let t7;
  if ($[15] !== onChange) {
    t7 = () => onChange(createEmptyDemandInspirationFilters());
    $[15] = onChange;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const clearAll = t7;
  let t8;
  if ($[17] !== className) {
    t8 = cn("rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5", className);
    $[17] = className;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== clearFacet || $[20] !== toggleSelection || $[21] !== value || $[22] !== visibleFacets) {
    let t10;
    if ($[24] !== clearFacet || $[25] !== toggleSelection || $[26] !== value) {
      t10 = facet_2 => <FacetPopover key={facet_2.key} facet={facet_2} selectedIds={value[facet_2.key] as string[]} onToggle={id_0 => toggleSelection(facet_2.key, id_0)} onClear={() => clearFacet(facet_2.key)} />;
      $[24] = clearFacet;
      $[25] = toggleSelection;
      $[26] = value;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    t9 = visibleFacets.map(t10);
    $[19] = clearFacet;
    $[20] = toggleSelection;
    $[21] = value;
    $[22] = visibleFacets;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[28] !== t9) {
    t10 = <div className="mt-4 hidden flex-wrap gap-2 md:flex">{t9}</div>;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const t11 = hasActiveFilters ? "secondary" : "outline";
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="inline-flex items-center gap-2"><SlidersHorizontal data-icon="inline-start" />Filter</span>;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== activeFilterCount || $[32] !== hasActiveFilters) {
    t13 = hasActiveFilters ? <Badge variant="default">{activeFilterCount}</Badge> : <ChevronDown data-icon="inline-end" />;
    $[31] = activeFilterCount;
    $[32] = hasActiveFilters;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t11 || $[35] !== t13) {
    t14 = <DrawerTrigger asChild={true}><Button type="button" variant={t11} size="lg" className="w-full cursor-pointer justify-between rounded-xl">{t12}{t13}</Button></DrawerTrigger>;
    $[34] = t11;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DrawerHeader className="text-left"><DrawerTitle>Filter inspiration</DrawerTitle><DrawerDescription>Select one or more options. Choices within a group broaden the results.</DrawerDescription></DrawerHeader>;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== toggleSelection || $[39] !== value || $[40] !== visibleFacets) {
    let t17;
    if ($[42] !== toggleSelection || $[43] !== value) {
      t17 = facet_3 => <FilterOptionList key={facet_3.key} facet={facet_3} selectedIds={value[facet_3.key] as string[]} idPrefix="mobile" showLegend={true} onToggle={id_1 => toggleSelection(facet_3.key, id_1)} />;
      $[42] = toggleSelection;
      $[43] = value;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    t16 = visibleFacets.map(t17);
    $[38] = toggleSelection;
    $[39] = value;
    $[40] = visibleFacets;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[45] !== t16) {
    t17 = <ScrollArea className="h-[55vh] px-4"><div className="flex flex-col gap-7 pb-6">{t16}</div></ScrollArea>;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setMobileOpen(false);
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const t19 = resultCount === 1 ? "example" : "examples";
  let t20;
  if ($[48] !== resultCount || $[49] !== t19) {
    t20 = <Button type="button" className="cursor-pointer" onClick={t18}>Show {resultCount} {t19}</Button>;
    $[48] = resultCount;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const t21 = !hasActiveFilters;
  let t22;
  if ($[51] !== clearAll || $[52] !== t21) {
    t22 = <Button type="button" variant="ghost" className="cursor-pointer" disabled={t21} onClick={clearAll}>Clear all filters</Button>;
    $[51] = clearAll;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t20 || $[55] !== t22) {
    t23 = <DrawerFooter className="border-t border-gray-200">{t20}{t22}</DrawerFooter>;
    $[54] = t20;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t17 || $[58] !== t23) {
    t24 = <DrawerContent>{t15}{t17}{t23}</DrawerContent>;
    $[57] = t17;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== mobileOpen || $[61] !== t14 || $[62] !== t24) {
    t25 = <div className="mt-4 md:hidden"><Drawer open={mobileOpen} onOpenChange={setMobileOpen}>{t14}{t24}</Drawer></div>;
    $[60] = mobileOpen;
    $[61] = t14;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== activeSelections || $[65] !== toggleSelection) {
    let t27;
    if ($[67] !== toggleSelection) {
      t27 = t28 => {
        const {
          facetKey,
          option: option_1
        } = t28;
        const FacetIcon = FACET_ICONS.get(facetKey);
        return <Badge key={`${facetKey}-${option_1.id}`} variant="secondary" className="gap-1 pl-2">{FacetIcon && <FacetIcon className="size-3 shrink-0 text-gray-500" aria-hidden="true" />}{option_1.label}<button type="button" className="cursor-pointer rounded-full p-0.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400" aria-label={`Remove ${option_1.label} filter`} onClick={() => toggleSelection(facetKey, option_1.id)}><X className="size-3" aria-hidden="true" /></button></Badge>;
      };
      $[67] = toggleSelection;
      $[68] = t27;
    } else {
      t27 = $[68];
    }
    t26 = activeSelections.map(t27);
    $[64] = activeSelections;
    $[65] = toggleSelection;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[69] !== clearAll || $[70] !== hasActiveFilters) {
    t27 = hasActiveFilters && <Button type="button" variant="ghost" size="sm" className="cursor-pointer text-gray-500" onClick={clearAll}>Clear all</Button>;
    $[69] = clearAll;
    $[70] = hasActiveFilters;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== t26 || $[73] !== t27) {
    t28 = <div className="mt-4 flex flex-wrap items-center gap-2" aria-live="polite">{t26}{t27}</div>;
    $[72] = t26;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== t10 || $[76] !== t25 || $[77] !== t28 || $[78] !== t8) {
    t29 = <section className={t8} aria-labelledby="demand-inspiration-filter-title">{t10}{t25}{t28}</section>;
    $[75] = t10;
    $[76] = t25;
    $[77] = t28;
    $[78] = t8;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  return t29;
}
function FacetPopover(t0) {
  const $ = _c(36);
  const {
    facet,
    selectedIds,
    onToggle,
    onClear
  } = t0;
  const t1 = selectedIds.length > 0 ? "secondary" : "outline";
  let t2;
  if ($[0] !== facet.icon) {
    t2 = <facet.icon data-icon="inline-start" />;
    $[0] = facet.icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== selectedIds.length) {
    t3 = selectedIds.length > 0 && <Badge variant="default">{selectedIds.length}</Badge>;
    $[2] = selectedIds.length;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronDown data-icon="inline-end" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== facet.label || $[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t5 = <PopoverTrigger asChild={true}><Button type="button" variant={t1} className="cursor-pointer rounded-full">{t2}{facet.label}{t3}{t4}</Button></PopoverTrigger>;
    $[5] = facet.label;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== facet.icon) {
    t6 = <facet.icon className="size-4 shrink-0 text-gray-500" aria-hidden="true" />;
    $[10] = facet.icon;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== facet.mobileLabel || $[13] !== t6) {
    t7 = <p className="inline-flex items-center gap-2 font-bold text-gray-950">{t6}{facet.mobileLabel}</p>;
    $[12] = facet.mobileLabel;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== onClear || $[16] !== selectedIds.length) {
    t8 = selectedIds.length > 0 && <Button type="button" variant="ghost" size="sm" className="cursor-pointer text-gray-500" onClick={onClear}>Clear</Button>;
    $[15] = onClear;
    $[16] = selectedIds.length;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">{t7}{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = facet.options.length > 7 ? "h-72" : "h-auto";
  let t11;
  if ($[21] !== t10) {
    t11 = cn(t10);
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== facet || $[24] !== onToggle || $[25] !== selectedIds) {
    t12 = <div className="p-2"><FilterOptionList facet={facet} selectedIds={selectedIds} idPrefix="desktop" onToggle={onToggle} /></div>;
    $[23] = facet;
    $[24] = onToggle;
    $[25] = selectedIds;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11 || $[28] !== t12) {
    t13 = <ScrollArea className={t11}>{t12}</ScrollArea>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13 || $[31] !== t9) {
    t14 = <PopoverContent align="start" className="w-80 p-0">{t9}{t13}</PopoverContent>;
    $[30] = t13;
    $[31] = t9;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t14 || $[34] !== t5) {
    t15 = <Popover>{t5}{t14}</Popover>;
    $[33] = t14;
    $[34] = t5;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
}
function FilterOptionList(t0) {
  const $ = _c(15);
  const {
    facet,
    selectedIds,
    idPrefix,
    showLegend: t1,
    onToggle
  } = t0;
  const showLegend = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== facet.options) {
    const groups = new Map();
    facet.options.forEach(option => {
      const group = option.groupLabel ?? "";
      groups.set(group, [...(groups.get(group) ?? []), option]);
    });
    t2 = Array.from(groups.entries());
    $[0] = facet.options;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const groupedOptions = t2;
  let t3;
  if ($[2] !== facet.icon || $[3] !== facet.mobileLabel || $[4] !== showLegend) {
    t3 = showLegend && <FieldLegend className="inline-flex items-center gap-2"><facet.icon className="size-4 shrink-0 text-gray-500" aria-hidden="true" />{facet.mobileLabel}</FieldLegend>;
    $[2] = facet.icon;
    $[3] = facet.mobileLabel;
    $[4] = showLegend;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== facet.key || $[7] !== groupedOptions || $[8] !== idPrefix || $[9] !== onToggle || $[10] !== selectedIds) {
    t4 = groupedOptions.map(t5 => {
      const [groupLabel, options] = t5;
      return <div key={groupLabel || facet.key} className="flex flex-col gap-1">{groupLabel && <p className="px-2 pt-2 text-sm font-bold text-gray-500">{groupLabel}</p>}<FieldGroup className="gap-0.5">{options.map(option_0 => {
            const checkboxId = `${idPrefix}-${facet.key}-${option_0.id}`;
            const checked = selectedIds.includes(option_0.id);
            return <Field key={option_0.id} orientation="horizontal" className="rounded-lg px-2 py-2.5 transition-colors hover:bg-gray-50"><Checkbox id={checkboxId} checked={checked} className="cursor-pointer" onCheckedChange={() => onToggle(option_0.id)} /><FieldLabel htmlFor={checkboxId} className="cursor-pointer text-sm font-medium">{option_0.label}</FieldLabel></Field>;
          })}</FieldGroup></div>;
    });
    $[6] = facet.key;
    $[7] = groupedOptions;
    $[8] = idPrefix;
    $[9] = onToggle;
    $[10] = selectedIds;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <FieldSet className="gap-3">{t3}{t4}</FieldSet>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}